"use client"

import { isAxiosError } from "axios"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Bot, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { addAutoTradingStock } from "@/lib/api/autotrading"

interface AddToAutoTradingButtonProps {
  stockCode: string
  added: boolean
  className?: string
  strategyType?: string
  tooltipLabel?: string
  addedTooltipLabel?: string
  successMessage?: string
}

export function AddToAutoTradingButton({
  stockCode,
  added,
  className,
  strategyType,
  tooltipLabel = "자동매매 대상으로 추가",
  addedTooltipLabel = "이미 자동매매 대상으로 추가됨",
  successMessage = "자동매매 대상으로 추가되었습니다",
}: AddToAutoTradingButtonProps) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => addAutoTradingStock(stockCode, strategyType),
    onSuccess: () => {
      toast.success(successMessage)
      queryClient.invalidateQueries({ queryKey: ["backtest", "watchlist"] })
      queryClient.invalidateQueries({ queryKey: ["autotrading", "stocks"] })
    },
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.info("이미 추가된 종목입니다")
        queryClient.invalidateQueries({ queryKey: ["backtest", "watchlist"] })
        return
      }
      toast.error("자동매매 추가에 실패했습니다")
    },
  })

  const disabled = added || mutation.isPending

  return (
    <button
      type="button"
      disabled={disabled}
      title={added ? addedTooltipLabel : tooltipLabel}
      onClick={(e) => {
        e.stopPropagation()
        if (!added) mutation.mutate()
      }}
      className={cn(
        "inline-flex size-7 shrink-0 items-center justify-center rounded-md transition-colors",
        added
          ? "cursor-default text-emerald-500/70"
          : "text-zinc-400 hover:bg-zinc-800 hover:text-emerald-400",
        className
      )}
    >
      {mutation.isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Bot className="size-4" />
      )}
    </button>
  )
}
